import React, { FC, useCallback, useEffect, useState, forwardRef } from 'react'
import clsx from 'clsx'
import {
  NodeFormattingExtension,
  HorizontalRuleExtension,
  LinkExtension,
  BoldExtension,
  ItalicExtension,
  UnderlineExtension,
  HeadingExtension,
  TextColorExtension,
  IframeExtension,
  PlaceholderExtension,
  wysiwygPreset,
  SubExtension,
  SupExtension,
  ParagraphExtension,
  TextHighlightExtension,
  StrikeExtension,
  ImageExtension,
  MentionAtomExtension
} from 'remirror/extensions'
import {
  EditorComponent,
  FloatingWrapper,
  Remirror,
  useHelpers,
  useMentionAtom,
  useRemirror
} from '@remirror/react'
import { Icon } from '@iconify/react'

import { Avatar } from '../../atoms'
import Menu from './components/Menu'
import MinifiedMenu from './components/MinifiedMenu'
import EditorData from './components/EditorData'
import RefMaintainer from './components/RefMaintainer'
import EnterKepMap from './components/EnterKepMap'
import styles from './_.module.css'
import 'remirror/styles/all.css'

type Props = {
  placeholder?: string
  className?: string
  focusClassName?: string
  toolbar?: boolean
  getHtmlData?: boolean
  setHtmlData?: boolean
  updateData: (data: any, charCount?: number, plainText?: string) => void
  loadData?: string
  miniEditor?: boolean
  editable?: boolean
  mentionData?: RemirrorMentions[]
  maxLength?: number
  minLength?: number
  showTextAreaDisplay?: boolean
  onPressEnter?: () => void
  preserveText?: boolean
}

function UserSuggestor({ allUsers }: { allUsers: any }): JSX.Element {
  const [users, setUsers] = useState<RemirrorMentions[]>([])
  const { state, getMenuProps, getItemProps, indexIsHovered, indexIsSelected } =
    useMentionAtom({
      items: users
    })

  useEffect(() => {
    if (!state) {
      return
    }

    const searchTerm = state.query.full.toLowerCase()
    const filteredUsers = allUsers
      .filter((user: RemirrorMentions) =>
        user.label.toLowerCase().includes(searchTerm)
      )
      .sort()
    setUsers(filteredUsers)
  }, [state, allUsers])

  const enabled = !!state

  return (
    <FloatingWrapper
      positioner="cursor"
      enabled={enabled}
      placement="bottom-start"
      containerClass={clsx(styles.floatingWrapper)}
      renderOutsideEditor={true}
    >
      <div
        {...getMenuProps()}
        style={{
          maxHeight: '280px',
          width: 'auto',
          overflowY: 'auto',
          borderWidth: 'thin',
          borderStyle: 'solid'
        }}
        className="suggestions"
      >
        {enabled &&
          users.map((user, index) => {
            const isHighlighted = indexIsSelected(index)
            const isHovered = indexIsHovered(index)
            return (
              <div
                key={user.id}
                style={{
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                className={clsx(
                  !isHighlighted && !isHovered && styles.normalText,
                  isHighlighted && styles.isHighlighted,
                  isHovered && styles.isHovered
                )}
                {...getItemProps({
                  item: {
                    label: user.label,
                    id: user.id,
                    'data-mention-id': user['data-mention-id'],
                    avatar: user.avatar
                  },
                  index
                })}
              >
                <Avatar
                  size="small"
                  src={user.avatar}
                  icon={<Icon icon="ant-design:user-outlined" />}
                  alt={user.label ?? ''}
                />
                <span style={{ marginLeft: '5px' }}>{user.label}</span>
              </div>
            )
          })}
      </div>
    </FloatingWrapper>
  )
}
//** TOD0 - Refactor this component and extract the logic */
const Counter: FC<{ max: number; editorState: any; min?: number }> = ({
  max,
  min = 0,
  editorState
}) => {
  const { getText } = useHelpers(true)
  const text = getText(editorState)
  const length = text && text.length > 0 ? text.length : 0
  const hasMinLength = min > 0
  const minimumCharacterHelperText = hasMinLength && length <= min
  const hasExceededCharacterLimit = length > max

  return (
    <div className={styles.wordCount}>
      {!hasMinLength ? (
        <span className={clsx(length > max && styles.danger)}>
          {`${length}/${max}`}
        </span>
      ) : (
        <div className={styles.container}>
          <span className={styles.helperText}>
            {minimumCharacterHelperText
              ? 'The comment should be at-least 150 characters long'
              : hasExceededCharacterLimit
              ? 'Character Limit Exceeded'
              : ''}
          </span>
          <div
            className={clsx(
              (length > max || minimumCharacterHelperText) && styles.danger
            )}
          >
            {length > 0
              ? hasExceededCharacterLimit
                ? `+${length - max} characters`
                : minimumCharacterHelperText && `${length - min} characters`
              : null}
          </div>
        </div>
      )}
      {!hasMinLength && hasExceededCharacterLimit && (
        <span className={clsx(length > max && styles.danger)}>
          -Character Limit Exceeded
        </span>
      )}
    </div>
  )
}

// eslint-disable-next-line react/display-name
export const RichTextEditor = forwardRef(
  (
    {
      placeholder,
      className,
      focusClassName,
      toolbar,
      setHtmlData,
      getHtmlData,
      updateData,
      loadData,
      miniEditor,
      editable = true,
      mentionData = [],
      showTextAreaDisplay,
      maxLength,
      minLength = 0,
      onPressEnter,
      preserveText
    }: Props,
    ref
  ) => {
    const [editorState, setEditorState] = useState<any>(null)
    const [isFocus, setIsFocus] = useState(false)

    const extensions = useCallback(
      () => [
        new NodeFormattingExtension({}),
        new HorizontalRuleExtension({}),
        new LinkExtension({}),
        new BoldExtension({}),
        new ItalicExtension(),
        new HeadingExtension({}),
        new TextColorExtension({}),
        new TextHighlightExtension({}),
        new IframeExtension({ enableResizing: false }),
        new SubExtension(),
        new SupExtension(),
        new UnderlineExtension(),
        new ParagraphExtension(),
        new PlaceholderExtension({ placeholder }),
        new StrikeExtension(),
        new MentionAtomExtension({
          extraAttributes: {
            'data-mention-id': {
              default: () => 'empty'
            }
          },
          matchers: [{ name: 'at', char: '@', matchOffset: 0 }],
          mentionTag: 'span'
        }),
        new ImageExtension({
          enableResizing: true,
          uploadHandler: (_files: any) => {
            return []
          }
        }),
        ...wysiwygPreset()
      ],
      [placeholder]
    )

    const { manager, state, setState } = useRemirror({
      extensions,
      content: setHtmlData && loadData && loadData !== '' ? loadData : '',
      stringHandler: 'html',
      builtin: { persistentSelectionClass: 'selection' }
    })
    const focusedClassName = focusClassName ? focusClassName : styles.isFocus

    return (
      <div
        className={clsx(styles.editorWrap, className, {
          [styles.revertedEditor]: miniEditor,
          [styles.showTextAreaDisplay]: showTextAreaDisplay,
          [focusedClassName]: showTextAreaDisplay && isFocus
        })}
      >
        <Remirror
          manager={manager}
          initialContent={state}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          onChange={parameter => {
            setState(parameter.state)
            setEditorState(parameter.state)
          }}
          editable={editable}
        >
          <EnterKepMap onKeyPressEnter={onPressEnter} />
          <UserSuggestor allUsers={mentionData} />
          <RefMaintainer ref={ref} />
          {toolbar && !miniEditor && <Menu />}
          <EditorData
            loadData={
              setHtmlData && loadData && loadData !== '' ? loadData : ''
            }
            editorState={editorState}
            updateData={updateData}
            getHtmlData={getHtmlData}
            preserveText={preserveText}
          />
          <EditorComponent />
          {toolbar && miniEditor && <MinifiedMenu />}
          {maxLength && editorState && (
            <Counter
              max={maxLength}
              editorState={editorState}
              min={minLength}
            />
          )}
        </Remirror>
      </div>
    )
  }
)
